import React, { useState, useEffect } from "react";
import { bankingPartnerAPI } from "../../../services/AdminPortal/api";
import { toast } from "react-toastify";

export default function LoansTab({ partnerData }) {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch loans for this specific banking partner
  const fetchLoans = async () => {
    if (!partnerData?._id) return;
    
    setLoading(true);
    setError(null);
    try {
      const res = await bankingPartnerAPI.getById(partnerData._id);
      const data = res.data || res;
      
      // Map backend structure to table structure
      const mappedLoans = (data.loanDetails || []).map((loan, idx) => ({
        id: idx,
        loanType: loan.loanType,
        minAmount: loan.minLoanAmount,
        maxAmount: loan.maxLoanAmount,
        eligibility: loan.eligibilityCriteria,
        interestRate: loan.interestRate,
        linkedInsurance: loan.loanInsurance,
        status: loan.status || "Active",
      }));
      
      setLoans(mappedLoans);
    } catch (err) {
      console.error("Error fetching loans:", err);
      setError(err.message || "Failed to fetch loans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, [partnerData?._id]);

  // Handler to update loan status
  const handleLoanStatusChange = async (loan, newStatus, idx) => {
    if (!partnerData?._id) return;
    
    try {
      await bankingPartnerAPI.updateLoanStatus(partnerData._id, idx, newStatus);
      toast.success("Loan status updated successfully");
      // Refresh the loans data
      fetchLoans();
    } catch (err) {
      console.error("Loan status update error:", err);
      toast.error(err.message || "Failed to update loan status");
    }
  };

  if (loading) {
    return (
      <div className="bg-[#131313] border border-customBorder p-4 rounded-md">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#131313] border border-customBorder p-4 rounded-md">
        <div className="text-center py-8 text-red-400">
          <p>Error loading loans: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#131313] border border-customBorder p-4 rounded-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">Loan Products</h3>
        <span className="text-sm text-gray-400">
          {loans.length} loan{loans.length !== 1 ? "s" : ""} found
        </span>
      </div>

      {loans.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No loan products available for this banking partner.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-[#232323] text-customWhite">
                <th className="px-4 py-2">Loan Type</th>
                <th className="px-4 py-2">Min-Max Amount</th>
                <th className="px-4 py-2">Eligibility Criteria</th>
                <th className="px-4 py-2">Interest Rate</th>
                <th className="px-4 py-2">Linked Insurance</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((loan, idx) => (
                <tr
                  key={loan.id || idx}
                  className="border-b border-customBorder"
                >
                  <td className="px-4 py-2">{loan.loanType}</td>
                  <td className="px-4 py-2">
                    ₹{loan.minAmount?.toLocaleString("en-IN")} - ₹
                    {loan.maxAmount?.toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-2">{loan.eligibility}</td>
                  <td className="px-4 py-2">{loan.interestRate}%</td>
                  <td className="px-4 py-2">{loan.linkedInsurance}</td>
                  <td className="px-4 py-2">
                    <select
                      value={loan.status}
                      onChange={(e) =>
                        handleLoanStatusChange(loan, e.target.value, idx)
                      }
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-yellow-400 border shadow-sm
                        ${
                          loan.status === "Active"
                            ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                            : "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                        }`}
                      style={{ minWidth: 110, cursor: "pointer" }}
                    >
                      <option value="Active">Active</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
